import { Check, Search, SettingsInputCompositeOutlined } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, styled, TextField } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { useDebounce } from '../hooks/useDebounce';
import { IconLabel } from '../theme/CustomComponents';

const FilterControlsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
});

export type SearchFilter = {
  label: string;
  value: boolean;
  onToggle: () => void;
};

interface DebouncedSearchInputProps {
  value: string;
  valueUpdated: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  shouldFocus?: boolean;
  labelText?: string;
  filters?: SearchFilter[];
  additionalStyles?: { backgroundColor?: string };
}

const DebouncedSearchInput = ({
  value,
  valueUpdated,
  onFocus,
  onBlur,
  shouldFocus,
  labelText = 'Search',
  filters = [],
  additionalStyles = {},
}: DebouncedSearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [localSearchQuery, setLocalSearchQuery] = useState(value);
  const [anchorFilterContextMenu, setAnchorFilterContextMenu] = useState<null | HTMLElement>(null);

  const debouncedSearchQuery = useDebounce(localSearchQuery, 250);

  useEffect(() => {
    const normalized = debouncedSearchQuery.trim();

    if (normalized === value.trim()) return;

    valueUpdated(debouncedSearchQuery);
  }, [debouncedSearchQuery, value, valueUpdated]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchQuery(e.target.value);
  };

  useEffect(() => {
    if (inputRef.current && shouldFocus) {
      inputRef.current.focus();
    }
  }, [shouldFocus]);

  const handleFilterToggle = (filter: SearchFilter) => {
    setAnchorFilterContextMenu(null);
    filter.onToggle();
  };

  return (
    <FilterControlsContainer>
      <TextField
        inputRef={inputRef}
        type={'search'}
        onChange={handleSearchChange}
        defaultValue={localSearchQuery}
        label={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: '#070154',
              paddingLeft: '12px',
            }}
          >
            <Search />
            {labelText}
          </div>
        }
        InputLabelProps={{
          sx: {
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
            '&.MuiInputLabel-shrink': {
              top: '-9px',
              left: '14px',
              transform: 'scale(0.75)',
            },
          },
        }}
        autoComplete={'off'}
        fullWidth
        onFocus={onFocus}
        onBlur={onBlur}
        sx={{
          minWidth: '250px',
          input: { height: '40px', padding: '0px 10px' },
          ...additionalStyles,
        }}
      />
      {filters.length > 0 && (
        <div>
          <IconButton onClick={(event) => setAnchorFilterContextMenu(event.currentTarget)}>
            <SettingsInputCompositeOutlined sx={{ color: '#070154' }} />
          </IconButton>
          <Menu
            sx={{ mt: '32px' }}
            anchorEl={anchorFilterContextMenu}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            open={Boolean(anchorFilterContextMenu)}
            onClose={() => setAnchorFilterContextMenu(null)}
          >
            {filters.map((filter, index) => (
              <MenuItem key={index} onClick={() => handleFilterToggle(filter)}>
                <IconLabel>
                  {filter.value && <Check sx={{ height: '16px', width: '16px' }} />}
                  <div style={{ marginLeft: filter.value ? '8px' : '28px' }}>{filter.label}</div>
                </IconLabel>
              </MenuItem>
            ))}
          </Menu>
        </div>
      )}
    </FilterControlsContainer>
  );
};

export default DebouncedSearchInput;
